import {
    useEffect,
    useState,
    useRef
} from "react";
import {
    ActivityIndicator,
    ScrollView,
    View
} from "react-native";
import type SubEventEditRouteProp from "./type";
import type {
    ExtraNotification,
    SubEventType,
    FormItem
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    DateTimePicker,
    PageContainer,
    TextAreaInput,
    TextInput,
    SelectBox,
    Button,
    Dialog,
    Switch,
    Text
} from "ncore-ui-kit";
import {
    useNavigation,
    useRoute
} from "@react-navigation/native";
import type RootStackParamList from "../../../../../navigation/type";
import type {
    EditSubEventResponseType,
    EditSubEventRequestType
} from "../../../../../services/restAPI/actions/editSubEvent/type";
import type {
    GetSubEventForAdminResponseType
} from "../../../../../services/restAPI/actions/getSubEventForAdmin/type";
import {
    type NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ArrowLeftIcon,
    PlusIcon,
    SaveIcon,
    Trash2
} from "lucide-react-native";
import RESTServiceContext from "../../../../../services/restAPI/context";

const SubEventEdit = () => {
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const route = useRoute<SubEventEditRouteProp>();

    const params = route.params as unknown as Record<string, unknown>;
    const eventID = params?.eventID as string | undefined;
    const subEventID = params?.subEventID as string | undefined;

    const [
        startDate,
        setStartDate
    ] = useState<string>("");

    const [
        endDate,
        setEndDate
    ] = useState<string>("");

    const [
        eventLocation,
        setEventLocation
    ] = useState<string>("");

    const [
        entranceCode,
        setEntranceCode
    ] = useState<string>("");

    const [
        isOnlyMembers,
        setIsOnlyMembers
    ] = useState<boolean>(false);

    const [
        type,
        setType
    ] = useState<SubEventType>("standart");

    const [
        applicationStartDate,
        setApplicationStartDate
    ] = useState<string>("");

    const [
        applicationEndDate,
        setApplicationEndDate
    ] = useState<string>("");

    const [
        formID,
        setFormID
    ] = useState<string>("");

    const [
        startNotificationTitle,
        setStartNotificationTitle
    ] = useState<string>("");

    const [
        startNotificationDescription,
        setStartNotificationDescription
    ] = useState<string>("");

    const [
        finishNotificationDescription,
        setFinishNotificationDescription
    ] = useState<string>("");

    const [
        finishNotificationTitle,
        setFinishNotificationTitle
    ] = useState<string>("");

    const [
        applicationStartNotificationTitle,
        setApplicationStartNotificationTitle
    ] = useState<string>("");

    const [
        applicationStartNotificationDescription,
        setApplicationStartNotificationDescription
    ] = useState<string>("");

    const [
        applicationEndNotificationTitle,
        setApplicationEndNotificationTitle
    ] = useState<string>("");

    const [
        applicationEndNotificationDescription,
        setApplicationEndNotificationDescription
    ] = useState<string>("");

    const [
        formsData,
        setFormsData
    ] = useState<FormItem[]>([]);
    const [
        isThreeTModule,
        setIsThreeTModule
    ] = useState<boolean>(false);

    const [
        extraNotifications,
        setExtraNotifications
    ] = useState<ExtraNotification[]>([]);

    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(true);

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState<boolean>(false);

    const [
        isDialogVisible,
        setIsDialogVisible
    ] = useState<boolean>(false);

    const [
        initialData,
        setInitialData
    ] = useState<GetSubEventForAdminResponseType["payload"] | null>(null);

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    useEffect(() => {
        const checkModule = async () => {
            if (eventID) {
                try {
                    const result = await RESTServiceContext.action("getEventForAdmin", {
                        eventID
                    });
                    if (result.payload?.moduleName === "threeT") {
                        setIsThreeTModule(true);
                    }
                } catch {
                    setIsThreeTModule(false);
                }
            }
        };
        checkModule();
    }, [
        eventID
    ]);

    useEffect(() => {
        const fetchSubEventDetail = async () => {
            if (!subEventID) {
                setIsLoading(false);
                return;
            }
            try {
                const result = await RESTServiceContext.action("getSubEventForAdmin", {
                    eventSubID: subEventID as string,
                    eventID: eventID as string
                });

                const typedResult = result as unknown as GetSubEventForAdminResponseType;
                if(typedResult.payload) {
                    const data = typedResult.payload;

                    setInitialData(data);

                    setStartDate(data.startDate ? new Date(data.startDate).toISOString() : "");
                    setEndDate(data.endDate ? new Date(data.endDate).toISOString() : "");
                    setEventLocation(data.eventLocation || "");
                    setEntranceCode(data.entranceCode || "");
                    setIsOnlyMembers(Boolean(data.isOnlyMembers));
                    setType(data.type || "standart");

                    if (data.applicationStartDate) setApplicationStartDate(new Date(data.applicationStartDate).toISOString());
                    if (data.applicationEndDate) setApplicationEndDate(new Date(data.applicationEndDate).toISOString());
                    if (data.formID) setFormID(data.formID);

                    setFinishNotificationDescription(data.finishNotificationDescription || "");
                    setStartNotificationDescription(data.startNotificationDescription || "");
                    setFinishNotificationTitle(data.finishNotificationTitle || "");
                    setStartNotificationTitle(data.startNotificationTitle || "");
                    setApplicationStartNotificationTitle(data.applicationStartNotificationTitle || "");
                    setApplicationStartNotificationDescription(data.applicationStartNotificationDescription || "");
                    setApplicationEndNotificationTitle(data.applicationEndNotificationTitle || "");
                    setApplicationEndNotificationDescription(data.applicationEndNotificationDescription || "");

                    if (data.notifications && Array.isArray(data.notifications)) {
                        setExtraNotifications(data.notifications.map((n: { _id?: string; targetDate?: string; description?: string; title?: string; }) => ({
                            targetDate: n.targetDate ? new Date(n.targetDate).toISOString() : "",
                            description: n.description || "",
                            id: Math.random().toString(),
                            title: n.title || "",
                            _id: n._id
                        })));
                    }
                }
            } catch {
                NCoreUIKitSnackBar.open({
                    title: localize("events-error-save")
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchSubEventDetail();
    }, [
        subEventID
    ]);

    const formSelectRef = useRef<import("react").ElementRef<typeof SelectBox>>(null);

    useEffect(() => {
        if (type === "application") {
            const fetchForms = async () => {
                try {
                    const result = await RESTServiceContext.action("getAllFormsForAdmin", null);
                    if (result.payload) {
                        const mappedData = result.payload.map((f: { title: string; id: string; }) => {
                            return {
                                title: f.title,
                                value: f.id
                            };
                        });
                        const initialSelected = formID ? mappedData.filter(f => f.value === formID).map(f => ({
                            __title: f.title,
                            __key: f.value
                        })) : [];
                        setFormsData(mappedData);
                        formSelectRef.current?.updateData(mappedData, initialSelected);
                    }
                } catch (error) {
                    console.error("Error fetching forms", error);
                }
            };
            fetchForms();
        }
    }, [type]);

    const handleAddNotification = () => {
        setExtraNotifications([
            ...extraNotifications,
            {
                id: Math.random().toString(),
                description: "",
                targetDate: "",
                title: ""
            }
        ]);
    };

    const handleRemoveNotification = (id: string) => {
        setExtraNotifications(extraNotifications.filter(n => n.id !== id));
    };

    const handleUpdateNotification = (id: string, field: keyof ExtraNotification, value: string) => {
        setExtraNotifications(extraNotifications.map(n => {
            if (n.id === id) {
                return {
                    ...n,
                    [field]: value
                };
            }
            return n;
        }));
    };

    const handleSubmit = async () => {
        if(!initialData) {
            return;
        }

        const payload: Record<string, unknown> = {
            eventSubID: subEventID as string,
            eventID: eventID as string
        };

        const checkRequiredField = (
            current: string,
            initial: string,
            key: string,
            forceRequire: boolean = false
        ) => {
            if (forceRequire && !current) {
                NCoreUIKitSnackBar.open({
                    title: localize("events-alert-fields-required")
                });
                return false;
            }
            if (current !== initial) {
                payload[key] = current;
            }
            return true;
        };

        const _currentStartDate = startDate ? new Date(startDate).toISOString() : "";
        const _initialStartDate = initialData.startDate ? new Date(initialData.startDate).toISOString() : "";
        if(!checkRequiredField(_currentStartDate, _initialStartDate, "startDate", true)) {
            return;
        }

        const _currentEndDate = endDate ? new Date(endDate).toISOString() : "";
        const _initialEndDate = initialData.endDate ? new Date(initialData.endDate).toISOString() : "";
        if(!checkRequiredField(_currentEndDate, _initialEndDate, "endDate", true)) {
            return;
        }

        if(!checkRequiredField(eventLocation, initialData.eventLocation || "", "eventLocation", true)) {
            return;
        }

        if(!checkRequiredField(finishNotificationDescription, initialData.finishNotificationDescription || "", "finishNotificationDescription", true)) {
            return;
        }

        if(!checkRequiredField(startNotificationDescription, initialData.startNotificationDescription || "", "startNotificationDescription", true)) {
            return;
        }

        if(!checkRequiredField(finishNotificationTitle, initialData.finishNotificationTitle || "", "finishNotificationTitle", true)) {
            return;
        }

        if(!checkRequiredField(startNotificationTitle, initialData.startNotificationTitle || "", "startNotificationTitle", true)) {
            return;
        }

        if(isOnlyMembers !== Boolean(initialData.isOnlyMembers)) {
            payload.isOnlyMembers = isOnlyMembers;
        }

        if(type !== (initialData.type || "standart")) {
            payload.type = type;
        }

        if(type === "application") {
            const _currentAppStartDate = applicationStartDate ? new Date(applicationStartDate).toISOString() : "";
            const _initialAppStartDate = initialData.applicationStartDate ? new Date(initialData.applicationStartDate).toISOString() : "";
            if(!checkRequiredField(_currentAppStartDate, _initialAppStartDate, "applicationStartDate", true)) {
                return;
            }

            const _currentAppEndDate = applicationEndDate ? new Date(applicationEndDate).toISOString() : "";
            const _initialAppEndDate = initialData.applicationEndDate ? new Date(initialData.applicationEndDate).toISOString() : "";
            if(!checkRequiredField(_currentAppEndDate, _initialAppEndDate, "applicationEndDate", true)) {
                return;
            }

            if(!checkRequiredField(applicationStartNotificationDescription, initialData.applicationStartNotificationDescription || "", "applicationStartNotificationDescription", true)) {
                return;
            }

            if(!checkRequiredField(applicationEndNotificationDescription, initialData.applicationEndNotificationDescription || "", "applicationEndNotificationDescription", true)) {
                return;
            }

            if(!checkRequiredField(applicationStartNotificationTitle, initialData.applicationStartNotificationTitle || "", "applicationStartNotificationTitle", true)) {
                return;
            }

            if(!checkRequiredField(applicationEndNotificationTitle, initialData.applicationEndNotificationTitle || "", "applicationEndNotificationTitle", true)) {
                return;
            }

            if(!checkRequiredField(entranceCode, initialData.entranceCode || "", "entranceCode", true)) {
                return;
            }

            if(!checkRequiredField(formID, initialData.formID || "", "formID", true)) {
                return;
            }
        }

        const currentNotifications = extraNotifications.map(n => {
            const base = {
                targetDate: new Date(n.targetDate).toISOString(),
                description: n.description,
                title: n.title
            };
            if(n._id) {
                return {
                    ...base,
                    _id: n._id
                };
            }
            return base;
        });

        const _initNotifications = (initialData.notifications || []).map((n: { _id?: string; targetDate?: string; description?: string; title?: string; }) => {
            const base = {
                targetDate: n.targetDate ? new Date(n.targetDate).toISOString() : "",
                description: n.description || "",
                title: n.title || ""
            };
            if(n._id) {
                return {
                    ...base,
                    _id: n._id
                };
            }
            return base;
        });

        if(JSON.stringify(currentNotifications) !== JSON.stringify(_initNotifications)) {
            payload.notifications = currentNotifications;
        }

        if(Object.keys(payload).length === 2) {
            NCoreUIKitSnackBar.open({
                title: localize("events-success-save")
            });
            navigation.goBack();
            return;
        }

        setIsDialogVisible(true);
    };

    const handleConfirmSave = async () => {
        setIsDialogVisible(false);
        setIsSubmitting(true);

        const payload: Record<string, unknown> = {
            eventSubID: subEventID as string,
            eventID: eventID as string
        };

        const checkRequiredField = (
            current: string,
            initial: string,
            key: string,
            forceRequire: boolean = false
        ) => {
            if(forceRequire && !current) {
                return false;
            }

            if(current !== initial) {
                payload[key] = current;
            }
            return true;
        };

        if(!checkRequiredField(startDate, initialData?.startDate as string, "startDate", true)) {
            setIsSubmitting(false);
            return;
        }

        if(!checkRequiredField(endDate, initialData?.endDate as string, "endDate", true)) {
            setIsSubmitting(false);
            return;
        }

        checkRequiredField(eventLocation, initialData?.eventLocation as string, "eventLocation", true);

        if(type === "application") {
            if(!checkRequiredField(applicationStartDate, initialData?.applicationStartDate as string, "applicationStartDate", true)) {
                setIsSubmitting(false);
                return;
            }

            if(!checkRequiredField(applicationEndDate, initialData?.applicationEndDate as string, "applicationEndDate", true)) {
                setIsSubmitting(false);
                return;
            }

            checkRequiredField(formID, initialData?.formID as string, "formID", true);
            checkRequiredField(entranceCode, initialData?.entranceCode as string, "entranceCode", true);

            checkRequiredField(applicationStartNotificationTitle, initialData?.applicationStartNotificationTitle as string, "applicationStartNotificationTitle");
            checkRequiredField(applicationStartNotificationDescription, initialData?.applicationStartNotificationDescription as string, "applicationStartNotificationDescription");
            checkRequiredField(applicationEndNotificationTitle, initialData?.applicationEndNotificationTitle as string, "applicationEndNotificationTitle");
            checkRequiredField(applicationEndNotificationDescription, initialData?.applicationEndNotificationDescription as string, "applicationEndNotificationDescription");
        }

        checkRequiredField(startNotificationTitle, initialData?.startNotificationTitle as string, "startNotificationTitle");
        checkRequiredField(startNotificationDescription, initialData?.startNotificationDescription as string, "startNotificationDescription");
        checkRequiredField(finishNotificationTitle, initialData?.finishNotificationTitle as string, "finishNotificationTitle");
        checkRequiredField(finishNotificationDescription, initialData?.finishNotificationDescription as string, "finishNotificationDescription");

        if(isOnlyMembers !== initialData?.isOnlyMembers) {
            payload.isOnlyMembers = isOnlyMembers;
        }

        const currentNotifications = extraNotifications.map(n => {
            const base = {
                targetDate: new Date(n.targetDate).toISOString(),
                description: n.description,
                title: n.title
            };
            if(n._id) {
                return {
                    ...base,
                    _id: n._id
                };
            }
            return base;
        });

        const _initNotifications = (initialData?.notifications || []).map((n: { _id?: string; targetDate?: string; description?: string; title?: string; }) => {
            const base = {
                targetDate: n.targetDate ? new Date(n.targetDate).toISOString() : "",
                description: n.description || "",
                title: n.title || ""
            };
            if(n._id) {
                return {
                    ...base,
                    _id: n._id
                };
            }
            return base;
        });

        if(JSON.stringify(currentNotifications) !== JSON.stringify(_initNotifications)) {
            payload.notifications = currentNotifications;
        }

        try {
            const result = await RESTServiceContext.action("editSubEvent", payload as unknown as EditSubEventRequestType);
            const typedResult = result as unknown as EditSubEventResponseType;

            if (typedResult.code === 200) {
                NCoreUIKitSnackBar.open({
                    title: localize("events-success-save")
                });

                navigation.goBack();
            } else {
                NCoreUIKitSnackBar.open({
                    title: localize("events-error-save")
                });
                setIsSubmitting(false);
                return;
            }
        } catch {
            NCoreUIKitSnackBar.open({
                title: localize("events-error-save")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    alignItems: "center",
                    flexDirection: "row",
                    gap: spaces.spacingMd
                }
            ]}
        >
            <Button
                icon={() => <ArrowLeftIcon
                    color={colors.project.brandWhite}
                    size={24}
                />}
                customBorderColor="secondaryButton"
                onPress={() => navigation.goBack()}
                customColor="secondaryButton"
                spreadBehaviour="free"
                variant="filled"
            />
            <View
                style={stylesheet.flex1}
            >
                <Text
                    customColor={colors.content.text.high}
                    variant="titleLargeSize"
                >
                    {localize("events-edit-page-title")}
                </Text>
                <Text
                    customColor={colors.content.text.mid}
                    style={{
                        marginTop: spaces.spacingXs
                    }}
                    variant="bodyMediumSize"
                >
                    {localize("events-basic-info-subtitle")}
                </Text>
            </View>
        </View>;
    };

    const renderTimeAndLocation = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                {
                    marginTop: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.formCard,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        marginTop: spaces.spacingMd,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingLg
                    }
                ]}
            >
                <View
                    style={{
                        flexDirection: "column",
                        gap: spaces.spacingMd
                    }}
                >
                    <DateTimePicker
                        onChange={(val) => {
                            if (val.date) {
                                setStartDate(typeof val.date === "string" ? val.date : val.date.toISOString());
                            } else {
                                setStartDate("");
                            }
                        }}
                        initialDate={startDate ? new Date(startDate) : undefined}
                        title={localize("events-start-date")}
                        placeholder="gg.aa.yyyy ss:dd"
                        titleFormat="DD.MM.YYYY HH:mm"
                        isWorkWithRealtime={false}
                        spreadBehaviour="stretch"
                        pickerType="date-time"
                    />
                    <TextInput
                        title={localize("events-start-notification-title")}
                        onChangeText={setStartNotificationTitle}
                        value={startNotificationTitle}
                        spreadBehaviour="stretch"
                    />
                    <TextAreaInput
                        title={localize("events-start-notification-desc")}
                        onChangeText={setStartNotificationDescription}
                        value={startNotificationDescription}
                        isUpdateOnRealtime={true}
                    />
                    <TextInput
                        title={localize("events-finish-notification-title")}
                        onChangeText={setFinishNotificationTitle}
                        value={finishNotificationTitle}
                        spreadBehaviour="stretch"
                    />
                    <TextAreaInput
                        title={localize("events-finish-notification-desc")}
                        onChangeText={setFinishNotificationDescription}
                        value={finishNotificationDescription}
                        isUpdateOnRealtime={true}
                    />
                    <DateTimePicker
                        onChange={(val) => {
                            if (val.date) {
                                setEndDate(typeof val.date === "string" ? val.date : val.date.toISOString());
                            } else {
                                setEndDate("");
                            }
                        }}
                        initialDate={endDate ? new Date(endDate) : undefined}
                        title={localize("events-end-date")}
                        placeholder="gg.aa.yyyy ss:dd"
                        titleFormat="DD.MM.YYYY HH:mm"
                        isWorkWithRealtime={false}
                        spreadBehaviour="stretch"
                        pickerType="date-time"
                    />
                    <TextInput
                        title={localize("events-location")}
                        onChangeText={setEventLocation}
                        spreadBehaviour="stretch"
                        value={eventLocation}
                    />
                </View>
            </View>
        </View>;
    };

    const renderEntranceAndAttendance = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                {
                    marginTop: spaces.spacingXl
                }
            ]}
        >
            <View>
                <Text
                    customColor={colors.content.text.high}
                    style={stylesheet.sectionTitle}
                    variant="titleMediumSize"
                >
                    Giriş / Yoklama
                </Text>
                <Text
                    customColor={colors.content.text.mid}
                    style={{
                        marginTop: spaces.spacingXs
                    }}
                    variant="bodyMediumSize"
                >
                    Salon girişinde okutulacak giriş kodu.
                </Text>
            </View>
            <View
                style={[
                    stylesheet.formCard,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        marginTop: spaces.spacingMd,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingLg
                    }
                ]}
            >
                <View
                    style={stylesheet.qrCodeRow}
                >
                    <View
                        style={stylesheet.qrCodeInput}
                    >
                        <TextInput
                            title={localize("events-entrance-code") + " *"}
                            onChangeText={setEntranceCode}
                            spreadBehaviour="stretch"
                            value={entranceCode}
                        />
                    </View>
                    <Button
                        onPress={() => {
                            const newCode = "GM-" + Math.random().toString(36).substring(2, 6).toUpperCase();
                            setEntranceCode(newCode);
                        }}
                        customBorderColor="secondaryButton"
                        title={localize("events-refresh")}
                        style={{
                            marginLeft: spaces.spacingMd
                        }}
                        customColor="secondaryButton"
                        spreadBehaviour="free"
                        variant="filled"
                    />
                </View>
            </View>
        </View>;
    };

    const renderTypeSelection = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                {
                    marginTop: spaces.spacingXl
                }
            ]}
        >
            <Text
                customColor={colors.content.text.high}
                style={stylesheet.sectionTitle}
                variant="titleMediumSize"
            >
                {localize("events-type-section-title")}
            </Text>
            <View
                style={[
                    stylesheet.formCard,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        marginTop: spaces.spacingMd,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingLg
                    }
                ]}
            >
                <View
                    style={stylesheet.typeButtonGroup}
                >
                    <Button
                        customBorderColor={type === "standart" ? undefined : "secondaryButton"}
                        customColor={type === "standart" ? undefined : "secondaryButton"}
                        customTextColor={colors.project.brandWhite}
                        title={localize("events-type-standard")}
                        onPress={() => setType("standart")}
                        style={stylesheet.typeButton}
                        spreadBehaviour="free"
                        variant="filled"
                    />
                    <Button
                        customBorderColor={type === "application" ? undefined : "secondaryButton"}
                        customColor={type === "application" ? undefined : "secondaryButton"}
                        customTextColor={colors.project.brandWhite}
                        onPress={() => setType("application")}
                        title={localize("events-type-join")}
                        style={[
                            stylesheet.typeButton,
                            {
                                marginLeft: spaces.spacingMd
                            }
                        ]}
                        spreadBehaviour="free"
                        variant="filled"
                    />
                </View>
                {
                    type === "application" ?
                        <View
                            style={[
                                stylesheet.highlightedContainer,
                                {
                                    marginTop: spaces.spacingLg,
                                    padding: spaces.spacingLg,
                                    borderRadius: radiuses.md,
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <View
                                style={[
                                    stylesheet.sectionContainer,
                                    {
                                        gap: spaces.spacingMd
                                    }
                                ]}
                            >
                                <DateTimePicker
                                    onChange={(val) => {
                                        if (val.date) {
                                            setApplicationStartDate(typeof val.date === "string" ? val.date : val.date.toISOString());
                                        } else {
                                            setApplicationStartDate("");
                                        }
                                    }}
                                    initialDate={applicationStartDate ? new Date(applicationStartDate) : undefined}
                                    contentContainerStyle={{
                                        backgroundColor: colors.project.brandWhite,
                                        borderRadius: radiuses.md
                                    }}
                                    title={localize("events-app-start-date")}
                                    placeholder="gg.aa.yyyy ss:dd"
                                    titleFormat="DD.MM.YYYY HH:mm"
                                    isWorkWithRealtime={false}
                                    spreadBehaviour="stretch"
                                    pickerType="date-time"
                                />
                                <DateTimePicker
                                    onChange={(val) => {
                                        if (val.date) {
                                            setApplicationEndDate(typeof val.date === "string" ? val.date : val.date.toISOString());
                                        } else {
                                            setApplicationEndDate("");
                                        }
                                    }}
                                    initialDate={applicationEndDate ? new Date(applicationEndDate) : undefined}
                                    contentContainerStyle={{
                                        backgroundColor: colors.project.brandWhite,
                                        borderRadius: radiuses.md
                                    }}
                                    title={localize("events-app-end-date")}
                                    placeholder="gg.aa.yyyy ss:dd"
                                    titleFormat="DD.MM.YYYY HH:mm"
                                    isWorkWithRealtime={false}
                                    spreadBehaviour="stretch"
                                    pickerType="date-time"
                                />
                            </View>
                            <TextInput
                                title={localize("events-app-start-notification-title")}
                                onChangeText={setApplicationStartNotificationTitle}
                                value={applicationStartNotificationTitle}
                                customColor={colors.project.brandWhite}
                                spreadBehaviour="stretch"
                            />
                            <TextAreaInput
                                onChangeText={setApplicationStartNotificationDescription}
                                title={localize("events-app-start-notification-desc")}
                                value={applicationStartNotificationDescription}
                                customColor={colors.project.brandWhite}
                                isUpdateOnRealtime={true}
                            />
                            <TextInput
                                title={localize("events-app-end-notification-title")}
                                onChangeText={setApplicationEndNotificationTitle}
                                customColor={colors.project.brandWhite}
                                value={applicationEndNotificationTitle}
                                spreadBehaviour="stretch"
                            />
                            <TextAreaInput
                                onChangeText={setApplicationEndNotificationDescription}
                                title={localize("events-app-end-notification-desc")}
                                value={applicationEndNotificationDescription}
                                customColor={colors.project.brandWhite}
                                isUpdateOnRealtime={true}
                            />
                            {
                                isThreeTModule ?
                                    <View
                                        style={{
                                            backgroundColor: colors.content.container.danger,
                                            marginBottom: spaces.spacingXs,
                                            marginTop: spaces.spacingXl,
                                            borderRadius: radiuses.md,
                                            padding: spaces.spacingMd
                                        }}
                                    >
                                        <Text
                                            customColor={colors.content.text.danger}
                                            variant="bodyMediumSize"
                                        >
                                            * Sektör eşleştirme öncelikli 3T kullanılacaksa, seçeceğiniz formda 'customer_target_sectors' ve 'suplier_target_sectors' alanlarının bulunduğundan emin olun.
                                        </Text>
                                    </View>
                                :
                                    <View
                                        style={{
                                            marginTop: spaces.spacingXl
                                        }}
                                    />
                            }
                            <SelectBox
                                onChange={(selected) => {
                                    if (selected.length > 0 && selected[0]) {
                                        const item = selected[0] as unknown as { __key?: string, value?: string };
                                        setFormID(item.__key || item.value || "");
                                    } else {
                                        setFormID("");
                                    }
                                }}
                                initialSelectedItems={formsData.filter(f => f.value === formID).map(f => ({
                                    __title: f.title,
                                    __key: f.value
                                }))}
                                titleExtractor={(i: unknown) => (i as { title: string }).title}
                                keyExtractor={(i: unknown) => (i as { value: string }).value}
                                spreadBehaviour="stretch"
                                title="Başvuru Formu"
                                ref={formSelectRef}
                                data={formsData}
                            />
                        </View>
                    :
                        null
                }
            </View>
        </View>;
    };

    const renderAccessSettings = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                {
                    marginTop: spaces.spacingXl
                }
            ]}
        >
            <Text
                customColor={colors.content.text.high}
                style={stylesheet.sectionTitle}
                variant="titleMediumSize"
            >
                {localize("events-members-only-title")}
            </Text>
            <View
                style={[
                    stylesheet.formCard,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        marginTop: spaces.spacingMd,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingLg
                    }
                ]}
            >
                <View
                    style={stylesheet.switchRow}
                >
                    <View
                        style={stylesheet.switchTextContainer}
                    >
                        <Text
                            customColor={colors.content.text.high}
                            variant="bodyMediumSize"
                        >
                            {localize("events-members-only-subtitle")}
                        </Text>
                    </View>
                    <Switch
                        onPress={() => setIsOnlyMembers(!isOnlyMembers)}
                        customTheme={{
                            themeKey: "events_form" as never
                        }}
                        isActive={isOnlyMembers}
                        type="neutral"
                    />
                </View>
            </View>
        </View>;
    };

    const renderDynamicNotifications = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                {
                    marginTop: spaces.spacingXl
                }
            ]}
        >
            <View>
                <Text
                    customColor={colors.content.text.high}
                    style={stylesheet.sectionTitle}
                    variant="titleMediumSize"
                >
                    Bildirimler
                </Text>
            </View>
            {extraNotifications.map((notif) => <View
                style={[
                    stylesheet.formCard,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        marginTop: spaces.spacingMd,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingLg
                    }
                ]}
                key={notif.id}
            >
                <View
                    style={{
                        flexDirection: "column",
                        gap: spaces.spacingMd
                    }}
                >
                    <View
                        style={stylesheet.switchRow}
                    >
                        <View
                            style={{
                                backgroundColor: colors.content.container.subtle,
                                paddingHorizontal: spaces.spacingMd,
                                paddingVertical: spaces.spacingXs,
                                borderRadius: radiuses.full
                            }}
                        >
                            <Text
                                customColor={colors.content.text.high}
                                variant="labelMediumSize"
                            >
                                Bildirim
                            </Text>
                        </View>
                        <Button
                            onPress={() => handleRemoveNotification(notif.id)}
                            icon={({
                                color,
                                size
                            }) => <Trash2
                                color={colors.content.icon[color]}
                                size={size}
                            />}
                            spreadBehaviour="free"
                            type="danger"
                        />
                    </View>
                    <TextInput
                        onChangeText={(val) => handleUpdateNotification(notif.id, "title", val)}
                        title={localize("events-notification-title")}
                        value={notif.title}
                    />
                    <TextAreaInput
                        onChangeText={(val) => handleUpdateNotification(notif.id, "description", val)}
                        title={localize("events-notification-desc")}
                        isUpdateOnRealtime={true}
                        value={notif.description}
                    />
                    <DateTimePicker
                        onChange={(val) => {
                            if (val.date) {
                                handleUpdateNotification(notif.id, "targetDate", typeof val.date === "string" ? val.date : val.date.toISOString());
                            } else {
                                handleUpdateNotification(notif.id, "targetDate", "");
                            }
                        }}
                        initialDate={notif.targetDate ? new Date(notif.targetDate) : undefined}
                        title={localize("events-notification-date")}
                        placeholder="gg.aa.yyyy ss:dd"
                        titleFormat="DD.MM.YYYY HH:mm"
                        isWorkWithRealtime={false}
                        spreadBehaviour="stretch"
                        pickerType="date-time"
                    />
                </View>
            </View>)}
            <Button
                icon={({
                    size
                }) => <PlusIcon
                    color={colors.project.brandWhite}
                    size={size}
                />}
                customBorderColor="secondaryButton"
                onPress={handleAddNotification}
                style={{
                    marginTop: spaces.spacingMd
                }}
                customColor="secondaryButton"
                spreadBehaviour="stretch"
                title={localize("add")}
                variant="filled"
            />
        </View>;
    };

    if (isLoading) {
        return <View
            style={[
                stylesheet.container,
                {
                    justifyContent: "center",
                    alignItems: "center"
                }
            ]}
        >
            <ActivityIndicator
                color={colors.project.brandPrimary}
                size="large"
            />
        </View>;
    }

    if (isLoading) {
        return <View
            style={stylesheet.loadingContainer}
        >
            <ActivityIndicator
                color={colors.project.brandPrimary}
                size="large"
            />
        </View>;
    }

    return <PageContainer
        isScrollable={true}
    >
        <ScrollView
            showsVerticalScrollIndicator={false}
        >
            <View
                style={[
                    stylesheet.container,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                {renderHeader()}
                {renderTimeAndLocation()}
                {renderTypeSelection()}
                {type === "application" ? renderEntranceAndAttendance() : null}
                {renderAccessSettings()}
                {renderDynamicNotifications()}
                <View
                    style={[
                        stylesheet.footerActionContainer,
                        {
                            marginTop: spaces.spacingXl
                        }
                    ]}
                >
                    <Button
                        title={localize("events-cancel-button")}
                        customBorderColor="secondaryButton"
                        onPress={() => navigation.goBack()}
                        customColor="secondaryButton"
                        spreadBehaviour="free"
                        variant="filled"
                    />
                    <Button
                        icon={({
                            color,
                            size
                        }) => <SaveIcon
                            color={colors.project.brandWhite || colors.content.icon[color]}
                            size={size}
                        />}
                        customTextColor={colors.project.brandWhite}
                        title={localize("events-save-button")}
                        style={{
                            marginLeft: spaces.spacingMd
                        }}
                        isLoading={isSubmitting}
                        onPress={handleSubmit}
                        spreadBehaviour="free"
                        variant="filled"
                    />
                </View>
            </View>
        </ScrollView>
        <Dialog
            primaryButtonProps={{
                title: localize("events-create-confirm-yes"),
                onPress: handleConfirmSave
            }}
            secondaryButtonProps={{
                title: localize("events-create-confirm-no"),
                onPress: () => setIsDialogVisible(false),
                customColor: "secondaryButton",
                variant: "filled"
            }}
            onOverlayPress={() => setIsDialogVisible(false)}
            onClosed={() => setIsDialogVisible(false)}
            isVisible={isDialogVisible}
            variant="yes-no"
        >
            <View
                style={{
                    padding: spaces.spacingLg
                }}
            >
                <Text
                    variant="bodyMediumSize"
                >
                    {localize("events-sub-edit-confirm-desc")}
                </Text>
            </View>
        </Dialog>
    </PageContainer>;
};

export default SubEventEdit;
